import React, { useState, useEffect } from "react";
import { connect } from "react-redux";

import TextField from "@material-ui/core/TextField";
import PropTypes from "prop-types";
import Portfolio from "@meetyoo/components/src/components/Portfolio";

import clsx from "clsx";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import LinearProgress from "@material-ui/core/LinearProgress";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import ErrorModal from "@meetyoo/components/src/components/Modal/ErrorModal";
import {
  createBill,
  setErrorMessage,
  resetBillingError,
  resetContract,
  setSettleContractDate,
  setReserveContractDate,
  resetGetBillError,
} from "../../../actions/billing";
import LocalizedUtils, { moment } from "../../../utils/LocalizedUtils";

import { defaultPasscodeTheme } from "../../../constants/passCodeUtils";

export const ownMoment = moment();

const useStyles = makeStyles(theme => ({
  ...defaultPasscodeTheme(theme),
  innercontainer: {
    display: "inline-flex",
  },
  container: {
    textAlign: "center",
    display: "inline",
  },
  button: {
    margin: theme.spacing(3, 0, 2, 2),
  },
  binding: {
    padding: theme.spacing(4, 1, 1, 1),
  },
  patternValidate: {
    "&:invalid": {
      background: "#F08080",
    },
  },
  buttonSpace: {
    width: "200px",
  },
}));

export const BillingEventComponent = ({
  billingEventContract,
  createBillHandler,
  errorMessage,
  setErrorMessageHandler,
  resetBillingErrorHandler,
  getBillErrorResponse,
  requestBillingInProgress,
  resetContractHandler,
  requestResetContract,
  reserveContractDate,
  settleContractDate,
  setSettleContractDateFn,
  setReserveContractDateFn,
  resetGetBillErrorFn,
}) => {
  const classes = useStyles();
  const [openErrorModal, setOpenErrorModal] = useState(false);

  const [contractNoFrom, setContractNoFrom] = useState(0);
  const [contractNoTo, setContractNoTo] = useState(0);
  const [contractNoCount, setContractNoCount] = useState(0);
  const [makeRequest, setMakeRequest] = useState(false);
  const [postingDate, setPostingDate] = useState(settleContractDate || moment(ownMoment).tz("UTC"));
  const [resetContractMonth, setResetContractMonth] = useState(
    reserveContractDate ||
      moment(ownMoment)
        .startOf("month")
        .tz("UTC")
  );
  const [resetContractNo, setResetContractNo] = useState(0);

  useEffect(() => {
    setOpenErrorModal(errorMessage !== "");
    const request = async () => {
      if (makeRequest && contractNoCount <= contractNoTo && !requestBillingInProgress && !getBillErrorResponse) {
        setContractNoCount(contractNoCount + 1);
        await createBillHandler(contractNoCount, postingDate.format("DD.MM.YYYY"));
        if (contractNoCount + 1 > contractNoTo) setMakeRequest(false);
      }
    };
    if (getBillErrorResponse) setMakeRequest(false);
    request();
  }, [
    openErrorModal,
    errorMessage,
    makeRequest,
    setMakeRequest,
    contractNoFrom,
    contractNoTo,
    getBillErrorResponse,
    requestBillingInProgress,
    contractNoCount,
    createBillHandler,
    postingDate,
  ]);

  const handleCloseErrorModal = () => {
    setOpenErrorModal(false);
    setErrorMessageHandler("");
  };

  return [
    <Portfolio
      title="Ledger Entries zurücksetzen"
      key="container-contract_reset"
      component={
        <div className={classes.container}>
          <div>
            <div className={classes.innercontainer}>
              <TextField
                id="contract-reset-input"
                label="Contract Number"
                value={resetContractNo}
                onChange={event => setResetContractNo(event.target.value)}
                margin="normal"
                inputProps={{ className: classes.patternValidate, pattern: "[0-9]*" }}
              />
              <MuiPickersUtilsProvider libInstance={moment} utils={LocalizedUtils}>
                <KeyboardDatePicker
                  id="ledger-entry-starting-time"
                  label="Beginning of the month"
                  className={clsx(classes.textField, classes.lineExpander)}
                  ampm={false}
                  format="DD.MM.YYYY"
                  margin="normal"
                  value={new Date(resetContractMonth) || ""}
                  onChange={date => {
                    setResetContractMonth(date);
                    setReserveContractDateFn(date);
                  }}
                />
              </MuiPickersUtilsProvider>
            </div>
            <div className={classes.lineBreak} />
            <div className={classes.innercontainer}>
              <div className={classes.buttonSpace} />
              <Button
                disabled={requestResetContract}
                variant="contained"
                className={classes.button}
                onClick={() => {
                  resetContractHandler(resetContractNo, resetContractMonth.format("DD.MM.YYYY"));
                }}
              >
                Revert
              </Button>
            </div>
          </div>
        </div>
      }
    />,
    <Portfolio
      title="Verträge abrechnen"
      key="container-contract"
      component={
        <div className={classes.container}>
          <div>
            <div className={classes.innercontainer}>
              <TextField
                id="contract-input"
                label="Contract Number From"
                value={contractNoFrom.toString()}
                onChange={event => setContractNoFrom(parseInt(event.target.value, 10) || event.target.value)}
                margin="normal"
                inputProps={{ className: classes.patternValidate, pattern: "[0-9]*" }}
              />
              <div className={classes.binding}>-</div>
              <TextField
                id="contract-input"
                label="Contract Number To"
                value={contractNoTo.toString()}
                onChange={event => setContractNoTo(parseInt(event.target.value, 10) || event.target.value)}
                margin="normal"
                inputProps={{ className: classes.patternValidate, pattern: "[0-9]*" }}
              />
            </div>
            <div className={classes.lineBreak} />
            <MuiPickersUtilsProvider libInstance={moment} utils={LocalizedUtils}>
              <KeyboardDatePicker
                id="ledger-entry-starting-time"
                label="Date"
                className={clsx(classes.textField, classes.lineExpander)}
                ampm={false}
                format="DD.MM.YYYY"
                margin="normal"
                value={postingDate || ""}
                onChange={date => {
                  setPostingDate(date);
                  setSettleContractDateFn(date);
                }}
              />
            </MuiPickersUtilsProvider>
            <Button
              variant="contained"
              className={classes.button}
              onClick={() => {
                setContractNoCount(contractNoFrom);
                setMakeRequest(true);
                resetGetBillErrorFn();
                resetBillingErrorHandler();
              }}
            >
              Create Bill
            </Button>
            <div className={classes.linebreak} />

            {contractNoCount - 1 <= contractNoTo && makeRequest && (
              <div>
                <div>Doing Document Number: {contractNoCount - 1}</div>
                <LinearProgress
                  variant="determinate"
                  value={((contractNoCount - contractNoFrom) / (contractNoTo - contractNoFrom)) * 100}
                />
                <Button
                  variant="contained"
                  className={classes.button}
                  onClick={() => {
                    setMakeRequest(false);
                  }}
                >
                  STOP!
                </Button>
              </div>
            )}
            {getBillErrorResponse && <div>Problem on Document Number: {contractNoCount - 1}</div>}
            {contractNoCount > contractNoTo && !makeRequest && <div>All Jobs Done!</div>}
            {billingEventContract !== "" && <div>Document Number: {billingEventContract}</div>}
          </div>
        </div>
      }
    />,

    <ErrorModal
      key="error"
      handleCloseErrorModal={handleCloseErrorModal}
      openErrorModal={openErrorModal}
      errorMessage={errorMessage}
    />,
  ];
};

BillingEventComponent.defaultProps = {
  errorMessage: "",
  reserveContractDate: undefined,
  settleContractDate: undefined,
};

BillingEventComponent.propTypes = {
  billingEventContract: PropTypes.string.isRequired,
  createBillHandler: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  setErrorMessageHandler: PropTypes.func.isRequired,
  getBillErrorResponse: PropTypes.bool.isRequired,
  requestBillingInProgress: PropTypes.bool.isRequired,
  resetBillingErrorHandler: PropTypes.func.isRequired,
  // translate: PropTypes.object.isRequired,
  reserveContractDate: PropTypes.object,
  settleContractDate: PropTypes.object,
  setReserveContractDateFn: PropTypes.func.isRequired,
  setSettleContractDateFn: PropTypes.func.isRequired,
  resetGetBillErrorFn: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  billingEventContract: state.billing.billingEventContract,
  errorMessage: state.billing.errorMessage,
  getBillErrorResponse: state.billing.getBillErrorResponse,
  requestBillingInProgress: state.billing.requestBillingInProgress,
  requestResetContract: state.billing.requestResetContract,
  reserveContractDate: state.billing.reserveContractDate,
  settleContractDate: state.billing.settleContractDate,
});

const mapDispatchToProps = dispatch => ({
  setErrorMessageHandler: errorMessage => dispatch(setErrorMessage(errorMessage)),
  createBillHandler: async (contractNumber, postingDate) => {
    await dispatch(createBill(contractNumber, postingDate));
  },
  resetBillingErrorHandler: () => dispatch(resetBillingError()),
  resetContractHandler: (contractNo, postingDate) => dispatch(resetContract(contractNo, postingDate)),
  setReserveContractDateFn: date => dispatch(setReserveContractDate(date)),
  setSettleContractDateFn: date => dispatch(setSettleContractDate(date)),
  resetGetBillErrorFn: () => dispatch(resetGetBillError()),
});

const BillingEvent = connect(
  mapStateToProps,
  mapDispatchToProps
)(BillingEventComponent);

export default BillingEvent;
